import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Briefcase, Mail, Lock, ArrowRight, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useRecaptcha } from "../hooks/useRecaptcha";
import SEO from "../components/SEO";
import { trackLogin } from "../utils/analytics";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { executeRecaptcha } = useRecaptcha();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Execute reCAPTCHA v3
      const captchaToken = await executeRecaptcha('login');
      
      await login(formData.email, formData.password, captchaToken);
      trackLogin("email");
      toast.success("Connexion réussie !");
      navigate("/dashboard");
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Erreur de connexion";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <SEO 
            title="Connexion"
            description="Connectez-vous à votre compte DCE Express pour accéder à vos recommandations d'appels d'offres personnalisées."
            path="/login"
            noindex={true}
          />
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-bold text-xl text-slate-900">Mon DCE Express</span>
          </Link>

          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-500 hover:text-violet-600 transition-colors mb-6"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>

          <Card className="border-0 shadow-none">
            <CardHeader className="px-0">
              <CardTitle className="font-display text-3xl font-bold text-slate-900">
                Content de vous revoir
              </CardTitle>
              <CardDescription className="text-slate-600 text-base">
                Connectez-vous pour accéder à vos recommandations personnalisées
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="votre@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 h-12 border-slate-200 focus:ring-violet-500"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
                    <Link 
                      to="/forgot-password" 
                      className="text-sm text-violet-600 hover:text-violet-700"
                      data-testid="forgot-password-link"
                    >
                      Mot de passe oublié ?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 h-12 border-slate-200 focus:ring-violet-500"
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-violet-700 hover:bg-violet-800 text-white rounded-full text-base font-medium"
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Se connecter
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-slate-600">
                  Pas encore de compte ?{" "}
                  <Link 
                    to="/register" 
                    className="text-violet-700 hover:text-violet-800 font-medium"
                    data-testid="login-register-link"
                  >
                    Créer un compte
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Right Panel - Visual */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-900 items-center justify-center p-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-center text-white max-w-lg"
        >
          <div className="w-24 h-24 rounded-3xl bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-8">
            <Briefcase className="w-12 h-12 text-white" />
          </div>
          <h2 className="font-display text-3xl font-bold mb-4">
            Trouvez vos marchés publics
          </h2>
          <p className="text-slate-300 text-lg">
            Notre IA analyse votre profil et vous propose les appels d'offres les plus adaptés à votre entreprise.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
